import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Upload as UploadIcon } from 'lucide-react';
import AIContentGenerator from '../../../services/aiService';

const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs text-gray-600">{label}</span>
    <input {...props} className="mt-1 w-full rounded-xl border border-gray-200 p-2 focus:ring-2 focus:ring-black" />
  </label>
);

const Card = ({ title, right, children }) => (
  <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

function UploadFlow() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [formData, setFormData] = useState({
    campaignName: '',
    category: '',
    targetRegions: '',
    embargoDate: '',
    pressReleaseText: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAIGeneration = async (type) => {
    setIsGenerating(true);
    try {
      let result;
      switch (type) {
        case 'headline':
          result = await AIContentGenerator?.optimizeContent({
            content: formData?.pressReleaseText,
            contentType: 'press release',
            targetGoal: 'improve headline and engagement'
          });
          setGeneratedContent({ type: 'headline', data: result });
          break;
        case 'quote':
          result = await AIContentGenerator?.generatePressRelease({
            topic: formData?.campaignName,
            company: 'PenGame Publishing',
            industry: formData?.category,
            keyPoints: ['Innovation', 'Market Leadership', 'Customer Value'],
            tone: 'professional'
          });
          setGeneratedContent({ type: 'quote', data: result });
          break;
        case 'seo':
          result = await AIContentGenerator?.optimizeContent({
            content: formData?.pressReleaseText,
            contentType: 'press release',
            targetGoal: 'SEO optimization'
          });
          setGeneratedContent({ type: 'seo', data: result });
          break;
        case 'localization':
          result = await AIContentGenerator?.generateStoryAngles({
            topic: formData?.campaignName,
            industry: formData?.category,
            newsHook: 'Regional business expansion',
            targetMedia: ['Local News', 'Regional Publications']
          });
          setGeneratedContent({ type: 'localization', data: result });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('AI generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const aiEnhancements = [
    { title: "Headline Optimizer", desc: "Score & improve for CTR/SEO", type: "headline" },
    { title: "Quote Generator", desc: "Exec quotes w/ tone match", type: "quote" },
    { title: "SEO Tags", desc: "Keywords, schema, UTM links", type: "seo" },
    { title: "Localization", desc: "Geo & outlet‑specific variants", type: "localization" },
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card title="Upload or Paste Your Press Release" right={null}>
          <div className="border-2 border-dashed rounded-2xl p-8 text-center">
            <UploadIcon className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="mb-2 font-medium">Drag & drop .docx, .pdf, or paste text</p>
            <p className="text-sm text-gray-500 mb-4">We auto-detect headings, quotes, boilerplate, and contact info</p>
            <button className="px-4 py-2 rounded-xl bg-black text-white hover:bg-gray-800 transition">
              Choose File
            </button>
          </div>

          <div className="mt-4">
            <label className="text-sm font-medium">Or paste text</label>
            <textarea 
              className="mt-2 w-full rounded-2xl border-gray-200 focus:ring-2 focus:ring-black p-4" 
              rows={8} 
              placeholder="Paste your release here..."
              value={formData?.pressReleaseText}
              onChange={(e) => handleInputChange('pressReleaseText', e?.target?.value)}
            />
          </div>
        </Card>

        <Card title="AI Enhancements">
          <div className="grid sm:grid-cols-2 gap-4">
            {aiEnhancements?.map((enhancement, index) => (
              <motion.div 
                key={index} 
                className="border rounded-2xl p-4 cursor-pointer hover:shadow-md transition"
                whileHover={{ scale: 1.02 }}
                onClick={() => handleAIGeneration(enhancement?.type)}
              >
                <p className="font-medium">{enhancement?.title}</p>
                <p className="text-sm text-gray-500">{enhancement?.desc}</p>
                {isGenerating && (
                  <div className="mt-2 text-xs text-blue-600">Generating...</div>
                )}
              </motion.div>
            ))}
          </div>

          {generatedContent && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200"
            >
              <h4 className="font-semibold text-green-800 mb-2">
                AI Generated {generatedContent?.type}
              </h4>
              <div className="text-sm text-green-700">
                {generatedContent?.type === 'headline' && (
                  <p>{generatedContent?.data?.optimized_headline}</p>
                )}
                {generatedContent?.type === 'quote' && (
                  <p>"{generatedContent?.data?.boilerplate}"</p>
                )}
                {generatedContent?.type === 'seo' && (
                  <div>
                    Keywords: {generatedContent?.data?.seo_recommendations?.join(', ')}
                  </div>
                )}
                {generatedContent?.type === 'localization' && (
                  <div>
                    {generatedContent?.data?.angles?.[0]?.angle_title}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </Card>
      </div>
      <div className="space-y-6">
        <Card title="Metadata">
          <div className="grid gap-3 text-sm">
            <Input 
              label="Campaign Name" 
              placeholder="Book Launch – Coloring Odyssey"
              value={formData?.campaignName}
              onChange={(e) => handleInputChange('campaignName', e?.target?.value)}
            />
            <Input 
              label="Category" 
              placeholder="Books / Music / Tech / Events"
              value={formData?.category}
              onChange={(e) => handleInputChange('category', e?.target?.value)}
            />
            <Input 
              label="Target Regions" 
              placeholder="Atlanta, Southeast, National"
              value={formData?.targetRegions}
              onChange={(e) => handleInputChange('targetRegions', e?.target?.value)}
            />
            <Input 
              label="Embargo Date" 
              placeholder="2025‑11‑12 10:00 ET"
              type="datetime-local"
              value={formData?.embargoDate}
              onChange={(e) => handleInputChange('embargoDate', e?.target?.value)}
            />
          </div>
        </Card>

        <Card title="Quality Checks" right={<span className="text-xs text-gray-500">Auto</span>}>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2 text-green-700">
              <CheckCircle2 size={16}/> AP style basics
            </li>
            <li className="flex items-center gap-2 text-green-700">
              <CheckCircle2 size={16}/> Contact & boilerplate found
            </li>
            <li className="flex items-center gap-2 text-amber-700">
              <AlertCircle size={16}/> Add 1 data point/stat
            </li>
          </ul>
        </Card>

        <button className="w-full py-3 rounded-2xl bg-black text-white font-semibold hover:bg-gray-800 transition">
          Save & Continue
        </button>
      </div>
    </div>
  );
}

export default UploadFlow;